/**
 *  Custom inline header search.
 *  Loaded only when settings.search_style == 'custom'.
 *
 *  Each <search-inline> instance owns its own input, panel and request state, so the
 *  desktop and mobile instances can coexist without talking to each other. Fetched
 *  markup is shared through a module-level cache since both hit the same endpoint.
 */
(() => {
  const RESULT_CACHE = new Map();
  const CACHE_LIMIT = 50;

  const debounceFn =
    typeof debounce === 'function'
      ? debounce
      : (fn, wait) => {
          let t;
          return (...args) => {
            clearTimeout(t);
            t = setTimeout(() => fn(...args), wait);
          };
        };

  if (customElements.get('search-inline')) return;

  class SearchInline extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('.search-inline__form');
      this.input = this.querySelector('.search-inline__input');
      this.panel = this.querySelector('.search-inline__panel');
      this.results = this.querySelector('.search-inline__results');
      this.initial = this.querySelector('.search-inline__initial');
      this.clearButton = this.querySelector('.search-inline__clear');

      if (!this.form || !this.input || !this.panel) return;

      this.controller = null;
      this.activeIndex = -1;
      this.isOpen = false;

      this.onDocumentPointerDown = this.onDocumentPointerDown.bind(this);
      this.onDocumentKeydown = this.onDocumentKeydown.bind(this);

      this.form.addEventListener('submit', this.onSubmit.bind(this));
      this.input.addEventListener('focus', this.onFocus.bind(this));
      this.input.addEventListener('keydown', this.onInputKeydown.bind(this));
      // The clear button tracks the field immediately; the fetch is debounced.
      this.input.addEventListener('input', () => this.toggleClearButton());
      this.input.addEventListener(
        'input',
        debounceFn(() => this.onInput(), 300)
      );

      this.clearButton?.addEventListener('click', () => this.clear());

      // Reflect the value the page loaded with (search results page).
      this.toggleClearButton();
    }

    disconnectedCallback() {
      this.controller?.abort();
      document.removeEventListener('pointerdown', this.onDocumentPointerDown);
      document.removeEventListener('keydown', this.onDocumentKeydown);
    }

    get query() {
      return this.input.value.trim();
    }

    /* ---------- open / close ---------- */

    open() {
      if (this.isOpen) return;
      // Nothing to show yet — don't flash an empty card.
      if (!this.hasPanelContent()) return;

      this.isOpen = true;
      this.panel.hidden = false;
      this.classList.add('is-open');
      this.input.setAttribute('aria-expanded', 'true');
      document.addEventListener('pointerdown', this.onDocumentPointerDown);
      document.addEventListener('keydown', this.onDocumentKeydown);
    }

    close() {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.panel.hidden = true;
      this.classList.remove('is-open');
      this.input.setAttribute('aria-expanded', 'false');
      this.setActive(-1);
      document.removeEventListener('pointerdown', this.onDocumentPointerDown);
      document.removeEventListener('keydown', this.onDocumentKeydown);
    }

    // The panel is worth opening when it has popular searches, fetched results, or a
    // spinner in flight. Without this an empty panel would flash open on focus.
    hasPanelContent() {
      if (this.classList.contains('is-loading')) return true;
      const hasResults = Boolean(this.results) && this.results.innerHTML.trim().length > 0;
      if (this.classList.contains('has-results')) return hasResults;
      return Boolean(this.initial) || hasResults;
    }

    // Open or close to match what the panel currently holds.
    refreshPanel() {
      if (this.hasPanelContent()) {
        this.open();
      } else {
        this.close();
      }
    }

    onFocus() {
      this.open();
      if (this.query.length) this.onInput();
    }

    onDocumentPointerDown(event) {
      if (!this.contains(event.target)) this.close();
    }

    onDocumentKeydown(event) {
      if (event.key === 'Escape') {
        this.close();
        this.input.focus();
      }
    }

    /* ---------- input handling ---------- */

    onInput() {
      const term = this.query;
      this.toggleClearButton();

      if (!term.length) {
        this.showInitial();
        this.refreshPanel();
        return;
      }
      if (!this.results) return;

      this.getResults(term);
    }

    onSubmit(event) {
      if (!this.query.length) {
        event.preventDefault();
        return;
      }
      this.close();
    }

    clear() {
      this.input.value = '';
      this.controller?.abort();
      this.toggleClearButton();
      this.showInitial();
      this.input.focus();
      this.refreshPanel();
    }

    toggleClearButton() {
      if (this.clearButton) this.clearButton.hidden = this.input.value.length === 0;
    }

    showInitial() {
      if (this.results) this.results.innerHTML = '';
      this.classList.remove('has-results', 'is-loading');
      this.setActive(-1);
    }

    /* ---------- fetching ---------- */

    getResults(term) {
      this.controller?.abort();

      if (RESULT_CACHE.has(term)) {
        this.render(RESULT_CACHE.get(term));
        return;
      }

      this.controller = new AbortController();
      this.classList.add('is-loading', 'has-results');
      this.open();

      const params = new URLSearchParams({
        q: term,
        'resources[type]': 'product,article,query',
        'resources[limit]': '10',
        'resources[options][fields]': 'title,product_type,vendor,variants.title,variants.sku',
        section_id: 'predictive-search'
      });

      fetch(`${theme.routes.predictive_search_url}?${params.toString()}`, {
        signal: this.controller.signal
      })
        .then((response) => {
          if (!response.ok) throw new Error(response.status);
          return response.text();
        })
        .then((text) => {
          const markup =
            new DOMParser()
              .parseFromString(text, 'text/html')
              .querySelector('#shopify-section-predictive-search')?.innerHTML || '';

          if (RESULT_CACHE.size >= CACHE_LIMIT) {
            RESULT_CACHE.delete(RESULT_CACHE.keys().next().value);
          }
          RESULT_CACHE.set(term, markup);

          this.render(markup);
        })
        .catch((error) => {
          if (error.name === 'AbortError') return;
          this.classList.remove('is-loading');
          this.showInitial();
        });
    }

    render(markup) {
      if (!this.results) return;
      this.results.innerHTML = markup;
      this.classList.remove('is-loading');
      this.classList.toggle('has-results', markup.trim().length > 0);
      this.setActive(-1);
      this.refreshPanel();
    }

    /* ---------- keyboard navigation ---------- */

    get options() {
      if (!this.isOpen) return [];
      const selector = this.classList.contains('has-results')
        ? '.search-inline__view-all, .search-inline__suggestion, .search-inline__product'
        : '.search-inline__chip';
      return Array.from(this.panel.querySelectorAll(selector));
    }

    onInputKeydown(event) {
      const options = this.options;

      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        if (!options.length) return;
        event.preventDefault();
        const step = event.key === 'ArrowDown' ? 1 : -1;
        const next = (this.activeIndex + step + options.length + 1) % (options.length + 1);
        // index === options.length maps back to the input itself
        this.setActive(next === options.length ? -1 : next);
        return;
      }

      if (event.key === 'Enter' && this.activeIndex > -1) {
        const target = options[this.activeIndex];
        if (target) {
          event.preventDefault();
          target.click();
        }
      }
    }

    setActive(index) {
      this.panel
        .querySelectorAll('.is-active')
        .forEach((el) => el.classList.remove('is-active'));
      this.activeIndex = index;

      if (index < 0) return;
      const target = this.options[index];
      if (!target) return;
      target.classList.add('is-active');
      target.scrollIntoView({ block: 'nearest' });
    }
  }

  customElements.define('search-inline', SearchInline);
})();
